import {
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  validateBranchTargets,
  type SequenceBranchRule,
} from '@marketing-hub/shared';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import styles from './sequenceEditor.module.css';

type Props = {
  stepOrder: number;
  rules: SequenceBranchRule[];
  stepOrders: number[];
  allStepsForValidation: { step_order: number; branch_rules: SequenceBranchRule[] }[];
  onChange: (rules: SequenceBranchRule[]) => void;
  disabled?: boolean;
};

export function SequenceBranchRulesEditor({
  stepOrder,
  rules,
  stepOrders,
  allStepsForValidation,
  onChange,
  disabled,
}: Props) {
  const issues = validateBranchTargets(allStepsForValidation).filter((i) => i.step_order === stepOrder);

  function updateRule(index: number, patch: Partial<SequenceBranchRule>) {
    const next = rules.map((r, i) => (i === index ? ({ ...r, ...patch } as SequenceBranchRule) : r));
    onChange(next);
  }

  function addRule() {
    if (rules.length >= 10) return;
    onChange([
      ...rules,
      { type: 'lead_has_tag', tag: '', goto_step_order: Math.min(stepOrder + 1, Math.max(...stepOrders)) },
    ]);
  }

  return (
    <div className={styles.branchBlock}>
      <strong>Branch rules</strong>
      <p className={styles.simPath}>Als lead matcht → spring naar stap (eerste match wint).</p>
      {rules.map((rule, index) => (
        <div key={index} className={styles.branchRow}>
          <Field label="Type">
            <select
              className={styles.sequenceListBtn}
              style={{ padding: '8px 12px' }}
              disabled={disabled}
              value={rule.type}
              onChange={(e) => {
                const type = e.target.value as SequenceBranchRule['type'];
                if (type === 'lead_has_tag') {
                  updateRule(index, { type, tag: '', goto_step_order: rule.goto_step_order });
                } else {
                  updateRule(index, {
                    type,
                    statuses: ['contacted'],
                    goto_step_order: rule.goto_step_order,
                  });
                }
              }}
            >
              <option value="lead_has_tag">Lead heeft tag</option>
              <option value="lead_status_in">Status is</option>
              <option value="lead_status_not_in">Status is niet</option>
            </select>
          </Field>
          {rule.type === 'lead_has_tag' ? (
            <Field label="Tag">
              <TextInput
                value={rule.tag}
                disabled={disabled}
                onChange={(e) => updateRule(index, { tag: e.target.value })}
              />
            </Field>
          ) : (
            <Field label="Status(s)">
              <select
                multiple
                className={styles.sequenceListBtn}
                style={{ minHeight: '4.5rem', padding: '8px 12px' }}
                disabled={disabled}
                value={rule.statuses}
                onChange={(e) => {
                  const selected = [...e.target.selectedOptions].map((o) => o.value);
                  updateRule(index, { statuses: selected.length ? selected : ['contacted'] });
                }}
              >
                {LEAD_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {LEAD_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Ga naar stap #">
            <select
              className={styles.sequenceListBtn}
              style={{ padding: '8px 12px' }}
              disabled={disabled}
              value={rule.goto_step_order}
              onChange={(e) => updateRule(index, { goto_step_order: Number(e.target.value) })}
            >
              {stepOrders.map((o) => (
                <option key={o} value={o}>
                  Stap {o}
                </option>
              ))}
            </select>
          </Field>
          <Button type="button" variant="ghost" disabled={disabled} onClick={() => onChange(rules.filter((_, i) => i !== index))}>
            Verwijder
          </Button>
        </div>
      ))}
      {issues.map((i) => (
        <p key={i.message} className={styles.saveError}>
          {i.message}
        </p>
      ))}
      <Button type="button" variant="secondary" disabled={disabled || rules.length >= 10} onClick={addRule}>
        + Branch rule
      </Button>
    </div>
  );
}
